import { ButtonV2, DataGridRef, FilterV2, SearchV2, SortV2 } from '@cimplico/components';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageContainer from '../components/page-container/PageContainer';
import PageTitle from '../components/PageTitle';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import ArchiveWorkpapersConfirm from '../features/workpapers/ArchiveWorkpapersConfirm';
import ChangeWorkpapersVisibilityConfirm from '../features/workpapers/ChangeWorkpapersVisibilityConfirm';
import UnarchiveWorkpapersConfirm from '../features/workpapers/UnarchiveWorkpapersConfirm';
import WorkpapersTable from '../features/workpapers/WorkpapersTable';
import UpgradePrompt from '../components/upgrade-prompt/UpgradePrompt';
import { useUserContext } from '../providers/UserProvider';
import { useEntities } from '../queries/use-entities';
import { useUsers } from '../queries/use-users';
import { Workpaper, WorkpaperRoleEnum, WorkpaperStatusEnum } from '../types/workpaper';
import { SubscriptionTier } from '../types/firm';
import { useIsActiveSubscription } from '../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../hooks/use-is-firm-role';
import WorkpapersTabs from '../features/workpapers/WorkpapersTabs';
import GrowHeightContainer from '../components/GrowHeightContainer';
import { useWorkpaperCsvExport } from '../queries/use-workpaper-csv-export';
import { toastNotification } from '../components/toast/Toast';
import { ArrowDownTrayIcon } from '@heroicons/react/20/solid';
import { intercomEvents } from '../intercom-events';
import { useIntercom } from 'react-use-intercom';
import { useHasFeatureAccess } from '../hooks/use-has-feature-access';
import { noRemainingActiveEntities } from '../features/new-workpaper/utils';
import { useFirmContext } from '../providers/FirmProvider';
import { Entity } from '../types/entity';

const sortOptions = [
    { label: 'Due', value: 'dueDate' },
    { label: 'Period End', value: 'periodEnd' },
    { label: 'Name', value: 'name' },
    { label: 'Entity', value: 'entityName' },
    { label: 'Last Updated', value: 'updatedAt' },
    { label: 'Progress', value: 'progressPercent' },
];

const tabs = ['all', 'open', 'closed', 'your-work', 'archived'];

function WorkpapersPage(): JSX.Element {
    const featureAccess = { canTogglePrivate: useHasFeatureAccess('workpaper-toggle-private:write') };
    const [query, setQuery] = useSearchParams();
    const firm = useFirmContext();
    const tab = query.get('tab');
    const [statusIndex, setStatusIndex] = useState(!tab ? 1 : tabs.indexOf(tab) ?? 1);
    const { trackEvent } = useIntercom();
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('');
    const [filter, setFilter] = useState('');
    const [selectedWorkpapers, setSelectedWorkpapers] = useState<Workpaper[]>([]);
    const [action, setAction] = useState<'ARCHIVE' | 'UNARCHIVE' | 'PRIVATE' | 'PUBLIC' | 'UPGRADE' | undefined>(undefined);
    const activeWorkpaperIds = selectedWorkpapers.filter((wp) => wp.status === WorkpaperStatusEnum.ACTIVE).map((wp) => wp.id);
    const archivedWorkpaperIds = selectedWorkpapers.filter((wp) => wp.status === WorkpaperStatusEnum.ARCHIVED).map((wp) => wp.id);
    const tableRef = useRef<DataGridRef | null>(null);
    const { isExternalUser, isAdmin } = useIsFirmRole();
    const { id: userId } = useUserContext();
    const navigate = useNavigate();

    const canUnarchive = selectedWorkpapers.filter(
        (wp) => wp.status === WorkpaperStatusEnum.ARCHIVED && !noRemainingActiveEntities(firm, wp.entity as Entity, firm.subscription)
    );

    const status =
        statusIndex === 0
            ? undefined
            : statusIndex === 1
            ? WorkpaperStatusEnum.ACTIVE
            : statusIndex === 2
            ? WorkpaperStatusEnum.COMPLETE
            : statusIndex === 4
            ? WorkpaperStatusEnum.ARCHIVED
            : undefined;

    const downloadQuery = useWorkpaperCsvExport({ search, sort, filter, status, assigned: statusIndex === 3 ? userId : undefined });

    const activeSubscription = useIsActiveSubscription();

    function redirectToNewWorkpaper(): void {
        navigate('new');
    }

    const entitiesQuery = useEntities({ page: 1, search: '', sort: '', filter: '' });
    const usersQuery = useUsers({ page: 1, search: '', sort: '', filter: '' });

    const canMakePrivate = selectedWorkpapers.filter((sw) => {
        if (sw.private) {
            return false;
        }
        if (isAdmin || sw.assigned.find((a) => a.id === userId)?.role === WorkpaperRoleEnum.APPROVER) {
            return true;
        }
        return false;
    });

    const canMakePublic = selectedWorkpapers.filter((sw) => {
        if (!sw.private) {
            return false;
        }
        if (isAdmin || sw.assigned.find((a) => a.id === userId)?.role === WorkpaperRoleEnum.APPROVER) {
            return true;
        }
        return false;
    });

    function handleTabChange(idx: number): void {
        setStatusIndex(idx);
    }

    function download(): void {
        downloadQuery.mutate(undefined, {
            onSuccess: () => {
                // Send to intercom
                trackEvent(intercomEvents.workpaper.exportDataToCsv.event);
            },
            onError: (error) => {
                toastNotification('error', `${error.message}`);
            },
        });
    }
    useEffect(() => {
        // 0 Is "All"
        if (statusIndex === 0 && !query.get('status')) {
            setTimeout(() => {
                query.append('status', 'ACTIVE');
                query.append('status', 'COMPLETE');
                setQuery(query);
            });
        }
        // 3 Is "Your Work"
        else if (statusIndex === 3 && !query.get('status')) {
            setTimeout(() => {
                query.append('status', 'ACTIVE');

                setQuery(query);
            });
        }
    }, [statusIndex]);

    return (
        <PageContainer>
            {action === 'ARCHIVE' && (
                <ArchiveWorkpapersConfirm
                    workpaperIds={activeWorkpaperIds}
                    onClose={(): void => {
                        setAction(undefined);
                        tableRef.current?.clear();
                    }}
                />
            )}
            {action === 'UNARCHIVE' && (
                <UnarchiveWorkpapersConfirm
                    workpaperIds={archivedWorkpaperIds}
                    onClose={(): void => {
                        setAction(undefined);
                        tableRef.current?.clear();
                    }}
                />
            )}

            {(action === 'PRIVATE' || action === 'PUBLIC') && (
                <ChangeWorkpapersVisibilityConfirm
                    action={action}
                    workpaperIds={action === 'PRIVATE' ? canMakePrivate.map((p) => p.id) : canMakePublic.map((p) => p.id)}
                    onClose={(): void => {
                        setAction(undefined);
                        tableRef.current?.clear();
                    }}
                />
            )}

            {action === 'UPGRADE' && <UpgradePrompt targetTier={SubscriptionTier.ESSENTIAL} onClose={(): void => setAction(undefined)} />}

            <div className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <PageTitle title="Workpapers" />
                    {selectedWorkpapers.length === 0 && <ButtonV2 size="sm" label="New Workpaper" onClick={redirectToNewWorkpaper} />}
                    {selectedWorkpapers.length > 0 && (
                        <SecondaryPageActions maxActionsCount={5}>
                            {canMakePrivate.length > 0 && (
                                <SecondaryPageActions.Button
                                    label={`Make private (${canMakePrivate.length})`}
                                    disabled={!activeSubscription}
                                    disabledHint={!activeSubscription ? 'You do not have an active subscription.' : undefined}
                                    onClick={(): void => {
                                        if (!featureAccess.canTogglePrivate) {
                                            setAction('UPGRADE');
                                        } else {
                                            setAction('PRIVATE');
                                        }
                                    }}
                                />
                            )}

                            {canMakePublic.length > 0 && (
                                <SecondaryPageActions.Button
                                    label={`Make public (${canMakePublic.length})`}
                                    disabled={!activeSubscription}
                                    disabledHint={!activeSubscription ? 'You do not have an active subscription.' : undefined}
                                    onClick={(): void => {
                                        if (!featureAccess.canTogglePrivate) {
                                            setAction('UPGRADE');
                                        } else {
                                            setAction('PUBLIC');
                                        }
                                    }}
                                />
                            )}

                            <SecondaryPageActions.Button
                                label={`Unarchive (${canUnarchive.length})`}
                                disabled={
                                    !activeSubscription || archivedWorkpaperIds.length === 0 || isExternalUser || canUnarchive.length === 0
                                }
                                onClick={(): void => setAction('UNARCHIVE')}
                            />
                            <SecondaryPageActions.Button
                                type="danger"
                                label={`Archive (${activeWorkpaperIds.length})`}
                                disabled={!activeSubscription || activeWorkpaperIds.length === 0 || isExternalUser}
                                onClick={(): void => setAction('ARCHIVE')}
                            />
                        </SecondaryPageActions>
                    )}
                </div>
                <div className="mt-4">
                    <WorkpapersTabs onChange={handleTabChange} selectedTabIndex={statusIndex} />
                    <GrowHeightContainer className="flex flex-wrap items-center gap-2 mt-4">
                        <FilterV2
                            onChange={({ queryString }): void => {
                                setFilter(queryString);
                            }}
                        >
                            {(statusIndex === 0 || statusIndex === 3) && (
                                <FilterV2.CheckboxFilter
                                    options={[
                                        { label: 'Open', value: WorkpaperStatusEnum.ACTIVE },
                                        { label: 'Closed', value: WorkpaperStatusEnum.COMPLETE },
                                        { label: 'Archived', value: WorkpaperStatusEnum.ARCHIVED },
                                    ]}
                                    id="status"
                                    label="Status"
                                />
                            )}
                            <FilterV2.ToggleFilter prefix="is" id="overdue" label="Overdue" />
                            <FilterV2.SelectFilter
                                id="entity.name"
                                label="Entity"
                                values={[]}
                                options={entitiesQuery.data?.entities?.map((e) => ({ value: e.id, label: `${e.name}` })) ?? []}
                            />
                            {statusIndex !== 3 && (
                                <FilterV2.SelectFilter
                                    id="assigned"
                                    label="Assigned"
                                    values={[]}
                                    options={
                                        usersQuery.data?.users?.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` })) ?? []
                                    }
                                />
                            )}
                            <FilterV2.CheckboxFilter
                                id="certified"
                                label="Certified"
                                options={[
                                    { label: 'Preparer', value: 'PREPARER' },
                                    { label: 'Reviewer', value: 'REVIEWER' },
                                    { label: 'Approver', value: 'APPROVER' },
                                ]}
                            />
                            <FilterV2.DateFilter id="updatedAt" label="Last Updated" />
                            <FilterV2.DateFilter id="dueDate" label="Due Date" />
                            <FilterV2.NumericFilter id="progress" label="Progress%" />
                        </FilterV2>
                        <SearchV2 placeholder="Search workpapers..." onChange={setSearch} />
                        <SortV2
                            options={statusIndex === 0 ? [...sortOptions] : sortOptions}
                            defaultValue="Due"
                            defaultDirection="asc"
                            onChange={({ queryString }): void => {
                                setSort(queryString);
                            }}
                        />
                        <div className="ml-auto">
                            <ButtonV2
                                leftIcon={<ArrowDownTrayIcon />}
                                type="secondary"
                                size="sm"
                                label="Export"
                                onClick={download}
                                isDisabled={downloadQuery.isLoading}
                            />
                        </div>
                    </GrowHeightContainer>
                </div>
            </div>
            <section className="mt-4">
                <WorkpapersTable
                    search={search}
                    sort={sort}
                    filter={filter}
                    onSelect={setSelectedWorkpapers}
                    tableRef={tableRef}
                    tabIndex={statusIndex}
                />
            </section>
        </PageContainer>
    );
}

export default WorkpapersPage;
